import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Home, ArrowLeft, Search, Shield } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const NotFoundPage = () => {
  const { language } = useLanguage();

  const translations = {
    tr: {
      title: '404',
      subtitle: 'Sayfa Bulunamadı',
      description: 'Aradığınız sayfa mevcut değil veya taşınmış olabilir.',
      homeButton: 'Ana Sayfa',
      backButton: 'Geri Dön',
      searchHint: 'Belki şunları arıyorsunuzdur:',
      links: [
        { name: 'IP Listesi', href: '/ip-list' },
        { name: 'Ağ Araçları', href: '/tools' },
        { name: 'Kurulum Rehberi', href: '/setup' },
        { name: 'Özellikler', href: '/features' },
      ]
    },
    en: {
      title: '404',
      subtitle: 'Page Not Found',
      description: 'The page you are looking for does not exist or may have been moved.',
      homeButton: 'Home',
      backButton: 'Go Back',
      searchHint: 'Maybe you were looking for:',
      links: [
        { name: 'IP List', href: '/ip-list' },
        { name: 'Network Tools', href: '/tools' },
        { name: 'Setup Guide', href: '/setup' },
        { name: 'Features', href: '/features' },
      ]
    }
  };

  const txt = translations[language] || translations.en;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="text-center max-w-lg">
          {/* 404 Icon */}
          <div className="mb-8 relative">
            <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 flex items-center justify-center">
              <Shield className="h-16 w-16 text-emerald-500/50" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-6xl font-bold bg-gradient-to-r from-emerald-500 to-cyan-500 bg-clip-text text-transparent">
                {txt.title}
              </span>
            </div>
          </div>

          {/* Text */}
          <h1 className="text-3xl font-bold mb-3">{txt.subtitle}</h1>
          <p className="text-muted-foreground mb-8">{txt.description}</p>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
            <Button 
              asChild
              className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700"
            >
              <Link to="/">
                <Home className="h-4 w-4 mr-2" />
                {txt.homeButton}
              </Link>
            </Button>
            <Button 
              variant="outline" 
              onClick={() => window.history.back()}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {txt.backButton}
            </Button>
          </div>

          {/* Helpful Links */}
          <div className="border-t border-border pt-8">
            <p className="text-sm text-muted-foreground mb-4 flex items-center justify-center gap-2">
              <Search className="h-4 w-4" />
              {txt.searchHint}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {txt.links.map((link, index) => (
                <Button key={index} variant="ghost" size="sm" asChild>
                  <Link to={link.href}>{link.name}</Link>
                </Button>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NotFoundPage;
